import React from 'react'
import {
	Box,
	Button,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material'
import Image from 'next/image'
import Link from 'next/link'

import { TooltipCustom } from '#/shared'
import styles from '#/shared/styles/api-keys.module.scss'

import { ApiKeysPageState } from '../model/use-api-keys'

import { CopyButton,KeyItem, KeyItemLimitField } from './components/key-item'

type ApiKeysDesktopProps = {
	state: ApiKeysPageState
}

export const ApiKeysDesktop: React.FC<ApiKeysDesktopProps> = ({ state }) => {
	const { keyItems, disabled, setModalOpen, hasKeys, deleteKey } = state

	return (
		<Stack spacing={2} className={styles.wrap}>
			<Stack spacing={2} className='pd-30 bg-color-block border-radius-main'>
				<Typography className='text'>
					API-ключ — это инструмент, который идентифицирует пользователя или программу,
					запрашивающих доступ к API платформы. С помощью ключа можно отслеживать, кто и
					когда пользуется API, рассчитывать оплату.
				</Typography>
				<Box display='flex' alignItems='center'>
					<Button
						onClick={() => setModalOpen(true)}
						disabled={disabled}
						className='btn-classic'
						sx={{ width: '130px' }}
					>
						Создать ключ
					</Button>
					<Link href='https://air-docs.readthedocs.io/' target='_blank'>
						<Typography
							className='text'
							sx={{
								marginLeft: '15px',
								color: '#8280FF !important',
							}}
						>
							Документация
						</Typography>
					</Link>
				</Box>
			</Stack>

			{hasKeys ? (
				<TableContainer className='bg-color-block border-radius-main'
					sx={{
						padding: '24px 30px',
						overflowX: 'auto',
					}}
				>
					<Stack spacing={2}>
						<Typography className='title-block'>Мои ключи</Typography>
						<Table
							size='small'
							aria-label='api keys table'
							sx={{
								tableLayout: 'auto',
								minWidth: 760,
								'& thead th': {
									fontWeight: 600,
									whiteSpace: 'normal',
									lineHeight: 1.4,
									backgroundColor: 'rgba(130, 128, 255, 0.04)',
								},
								'& th, & td': {
									py: 1.5,
									px: 2,
									borderBottom: '1px solid rgba(102, 107, 127, 0.12)',
								},
							}}
						>
							<TableHead>
								<TableRow>
									<TableCell sx={{ minWidth: 140 }}>Имя</TableCell>
									<TableCell sx={{ minWidth: 220 }}>Ключ</TableCell>
									<TableCell sx={{ minWidth: 160 }}>Лимит токенов</TableCell>
									<TableCell sx={{ minWidth: 120 }}>Создан</TableCell>
									<TableCell sx={{ minWidth: 150 }}>{'Действителен\u00a0до'}</TableCell>
									<TableCell sx={{ minWidth: 120 }} align='center'>
										Действия
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{keyItems.map((item) => (
									<KeyRow key={item.id} {...item} onDelete={deleteKey} />
								))}
							</TableBody>
						</Table>
					</Stack>
				</TableContainer>
			) : (
				<EmptyState />
			)}
		</Stack>
	)
}

type KeyRowProps = {
	id: string
	name: string
	keyValue: string
	limit: string | number | null
	createdAt: string
	expiresAt: string | null
	onDelete: (name: string) => void | Promise<void>
}

const KeyRow: React.FC<KeyRowProps> = ({ id, name, keyValue, limit, createdAt, expiresAt, onDelete }) => (
	<KeyItem keyValue={keyValue} name={name} limit={limit} createdAt={createdAt} expiresAt={expiresAt} onDelete={onDelete}>
		{({ handleCopy, handleLimitChange, isCopy, limit: currentLimit }) => (
			<TableRow
				key={id}
				sx={{
					'&:last-child td, &:last-child th ': {
						border: 0,
					},
				}}
			>
				<TableCell component='th' scope='row' sx={{ verticalAlign: 'top' }}>
					<Typography className='text' sx={{ fontWeight: 500, whiteSpace: 'normal' }}>
						{name}
					</Typography>
				</TableCell>
				<TableCell
					sx={{
						color: '#8280FF !important',
						wordBreak: 'break-word',
						whiteSpace: 'normal',
					}}
				>
					<Typography className='text' sx={{ color: '#8280FF !important' }}>
						{keyValue}
					</Typography>
				</TableCell>
				<TableCell sx={{ verticalAlign: 'top', color: '#8280FF !important' }}>
					<KeyItemLimitField value={currentLimit} onChange={handleLimitChange} />
				</TableCell>
				<TableCell sx={{ verticalAlign: 'top' }}>
					<Typography className='text'>{createdAt}</Typography>
				</TableCell>
				<TableCell sx={{ verticalAlign: 'top', whiteSpace: 'normal' }}>
					<Typography className='text'>{expiresAt !== null ? expiresAt : 'Бессрочно'}</Typography>
				</TableCell>
				<TableCell sx={{ verticalAlign: 'top', minWidth: 120 }} align='center'>
					<Stack direction='row' spacing={1.5} justifyContent='center' flexWrap='nowrap'>
						<CopyButton isCopy={isCopy} onCopy={() => handleCopy(keyValue)} />
						<TooltipCustom title={'Удалить ключ'}>
							<Image
								onClick={() => onDelete(name)}
								className={styles.clickableIcon}
								src='/svg/main_menu/trash.svg'
								width={20}
								height={20}
								alt='Удалить'
							/>
						</TooltipCustom>
					</Stack>
				</TableCell>
			</TableRow>
		)}
	</KeyItem>
)

const EmptyState = () => (
	<Box height={140} width={'100%'} display='flex' alignItems='center' justifyContent='center'>
		<Box textAlign='center'>
			<Typography className='title-vspomogatel'>У вас пока нет ключей 😞</Typography>
			<Typography className='title-main-gray'>Создайте первый ключ</Typography>
		</Box>
	</Box>
)

